import { CountriesList } from '@/shared/constants/countries-list';
import { TDtoWrapper } from '@/shared/lib/forms/form-wrapper.types';
import { ContactDto, ContactType } from '@/shared/lib/sj-orm/models/contact.dto';
import { QuestionType } from '@/shared/lib/sj-orm/models/form/question.dto';

import {
  MetalAccountDetailsType,
  MetalAccountGeneralType,
} from '../../sj-orm/models/assets/categories';
import { MetalAccountType } from '../../sj-orm/models/assets/enums';
import { CollectionName } from '../../sj-orm/types';

import { assetsDocumentsBuilder, AssetsDocumentsPartType } from './builders/documents';
import { assetsOwnershipBuilder, AssetsOwnershipPartType } from './builders/ownership';
import { assetsProfitabilityBuilder, AssetsProfitabilityPartType } from './builders/profitability';
import { assetsStatisticsBuilder, AssetsStatisticsPartType } from './builders/statistics';
import { baseAssetFields } from './fields';

export type MetalAccount = MetalAccountFormGeneralType &
  MetalAccountFormDetailsType &
  Omit<AssetsOwnershipPartType, 'createdAt' | 'updatedAt'> &
  Omit<AssetsProfitabilityPartType, 'createdAt' | 'updatedAt'> &
  AssetsStatisticsPartType &
  Omit<AssetsDocumentsPartType, 'createdAt' | 'updatedAt'>;

export const MetalAccountTypeList = [
  {
    label: 'forms_assets:metalAccount.enums.metalType.platinum',
    key: MetalAccountType.platinum,
  },
  {
    label: 'forms_assets:metalAccount.enums.metalType.palladium',
    key: MetalAccountType.palladium,
  },
  {
    label: 'forms_assets:metalAccount.enums.metalType.gold',
    key: MetalAccountType.gold,
  },
  {
    label: 'forms_assets:metalAccount.enums.metalType.silver',
    key: MetalAccountType.silver,
  },
  {
    label: 'forms_assets:metalAccount.enums.metalType.other',
    key: MetalAccountType.other,
  },
];

type MetalAccountHelpGeneralType = {
  nickname: QuestionType.TEXT;
  country: QuestionType.SELECT_FROM_LIST;
  type: QuestionType.SELECT_FROM_LIST;
  otherType: QuestionType.TEXT;
  purchaseValue: QuestionType.CURRENCY_CALCULATOR;
  holder: QuestionType.PICK_ARRAY_OF_DTO;
};

type MetalAccountHelpDetailsType = {
  cif?: QuestionType.TEXT;
  iban?: QuestionType.TEXT;
  dateOfOpening?: QuestionType.DATE_TIME;
  fixedTermAccount?: QuestionType.CHECKBOX;
  activeUntil?: QuestionType.DATE_TIME;
  metalQuantity?: QuestionType.NUMBER;
  attachments?: QuestionType.FILE_UPLOAD;
};

type MetalAccountFormGeneralType = TDtoWrapper<
  MetalAccountGeneralType,
  MetalAccountHelpGeneralType
>;
type MetalAccountFormDetailsType = TDtoWrapper<
  MetalAccountDetailsType,
  MetalAccountHelpDetailsType
>;

const MetalAccountFormGeneral: MetalAccountFormGeneralType = {
  nickname: {
    type: QuestionType.TEXT,
    label: 'forms_assets:common.inputs.nickname.label',
    value: '',
    required: true,
    colSpan: 2,
  },
  country: {
    type: QuestionType.SELECT_FROM_LIST,
    label: 'forms_assets:common.inputs.country.label',
    tooltip: 'forms_assets:common.inputs.country.tooltip',
    items: CountriesList,
    value: '',
    required: true,
    withSearch: true,
  },
  type: {
    type: QuestionType.SELECT_FROM_LIST,
    label: 'forms_assets:metalAccount.inputs.type.label',
    items: MetalAccountTypeList,
    value: MetalAccountType.platinum,
    required: true,
  },
  otherType: {
    type: QuestionType.TEXT,
    label: 'forms_assets:metalAccount.inputs.otherType.label',
    showIf: {
      type: {
        value: MetalAccountType.other,
      },
    },
  },
  purchaseValue: {
    type: QuestionType.CURRENCY_CALCULATOR,
    label: 'forms_assets:metalAccount.inputs.marketPrice.label',
    tooltip: 'forms_assets:metalAccount.inputs.marketPrice.tooltip',
    equivalentValueTooltip: 'forms_assets:common.inputs.equivalentValue.tooltip',
    value: 0,
    required: true,
  },
  holder: {
    type: QuestionType.PICK_ONE_OF_DTOS,
    label: 'forms_assets:metalAccount.inputs.holder.label',
    colName: CollectionName.CONTACTS,
    value: [],
    tooltip: 'forms_assets:metalAccount.inputs.holder.tooltip',
    // required: true,
    filter: (contact) => (contact as unknown as ContactDto).type === ContactType.BANK,
    dtoType: [ContactType.BANK],
  },
};

const MetalAccountFormDetails: MetalAccountFormDetailsType = {
  cif: {
    type: QuestionType.TEXT,
    label: 'forms_assets:metalAccount.inputs.cif.label',
    order: 2,
  },
  iban: {
    type: QuestionType.TEXT,
    label: 'forms_assets:debitAccount.inputs.iban.label',
    order: 3,
  },
  dateOfOpening: {
    type: QuestionType.DATE_TIME,
    label: 'forms_assets:debitAccount.inputs.dateOfOpening.label',
    order: 4,
  },
  activeUntil: {
    type: QuestionType.DATE_TIME,
    label: 'forms_assets:debitAccount.inputs.activeUntil.label',
    tooltip: 'forms_assets:metalAccount.inputs.activeUntil.tooltip',
    disableIf: {
      fixedTermAccount: {
        value: true,
        shouldClean: true,
      },
    },
    order: 5,
  },
  fixedTermAccount: {
    type: QuestionType.CHECKBOX,
    label: 'forms_assets:debitAccount.inputs.fixedTermAccount.label',
    order: 6,
  },
  metalQuantity: {
    type: QuestionType.NUMBER,
    label: 'forms_assets:metalAccount.inputs.metalQuantity.label',
    order: 7,
  },
  attachments: baseAssetFields.attachments,
};

const MetalAccountForm = [
  MetalAccountFormGeneral,
  MetalAccountFormDetails,
  assetsOwnershipBuilder().form,
  assetsProfitabilityBuilder([]),
  assetsStatisticsBuilder([
    'taxAgentWithBroker',
    'taxAgentWithBrokerOther',
    'publicFundTaxAgent',
    'publicTaxAgentOther',
    'legalTaxAgent',
    'legalTaxAgentOther',
    'holderTaxAgent',
    'holderTaxAgentOther',
    'specificsOfTaxation',
    'profitabilityFact',
    'profitabilityPlan',
  ]).form,
  assetsDocumentsBuilder(),
];

export { MetalAccountForm };
